const normalizeName = (value = "") =>
  String(value || "").trim().toLowerCase();

export const CREDIT_TYPE_KEYS = {
  FEDERAL_WOTC: "federalWotc",
  FEDERAL_NON_WOTC: "federalNonWotc",
  STATE: "state",
};

const FEDERAL_WOTC_NAMES = [
  "federal - work opportunity tax credit (wotc)",
  "work opportunity tax credit",
  "wotc",
].map(normalizeName);

const FEDERAL_NON_WOTC_NAMES = [
  "federal - empowerment zone employer credit",
  "federal - employer credit",
  "federal - fica tip tax credit",
  "employer credit",
].map(normalizeName);

const FEDERAL_WOTC_SET = new Set(FEDERAL_WOTC_NAMES);
const FEDERAL_NON_WOTC_SET = new Set(FEDERAL_NON_WOTC_NAMES);
const FEDERAL_CREDIT_SET = new Set([
  ...FEDERAL_WOTC_SET,
  ...FEDERAL_NON_WOTC_SET,
]);

export const isFederalCreditName = (value) => {
  const normalized = normalizeName(value);
  if (!normalized) return false;
  return FEDERAL_CREDIT_SET.has(normalized);
};

const CREDIT_AMOUNT_FIELDS = [
  "projectedTaxCreditAmount",
  "estimatedCreditAmount",
  "estimatedAmount",
  "projectedAmount",
  "creditAmount",
  "amount",
  "value",
];

const getCreditType = (name) => {
  const normalized = normalizeName(name);
  if (!normalized) return CREDIT_TYPE_KEYS.STATE;
  if (FEDERAL_WOTC_SET.has(normalized)) return CREDIT_TYPE_KEYS.FEDERAL_WOTC;
  if (FEDERAL_NON_WOTC_SET.has(normalized))
    return CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC;
  return CREDIT_TYPE_KEYS.STATE;
};

export const resolveCreditTypeKey = (name) => getCreditType(name);

const CREDIT_TYPE_BY_ID = {
  26: CREDIT_TYPE_KEYS.FEDERAL_WOTC,
  //25: for fica(enable later)
  24: CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC,
};

export const resolveCreditTypeFromRecord = (credit = {}) => {
  const rawId = credit?.id ?? credit?.creditId;
  if (typeof rawId !== "undefined" && rawId !== null) {
    const parsedId = Number(rawId);
    if (Number.isFinite(parsedId) && CREDIT_TYPE_BY_ID[parsedId]) {
      return CREDIT_TYPE_BY_ID[parsedId];
    }
  }
  return getCreditType(credit?.name);
};

const parseNumericValue = (value) => {
  if (typeof value === "number") {
    return Number.isNaN(value) ? null : value;
  }
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.-]+/g, "");
    if (!cleaned) return null;
    const parsed = Number(cleaned);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const getCreditAmount = (credit = {}) => {
  for (const field of CREDIT_AMOUNT_FIELDS) {
    const raw = credit[field];
    if (raw === null || typeof raw === "undefined") continue;
    const parsed = parseNumericValue(raw);
    if (parsed !== null) {
      return parsed;
    }
  }
  return null;
};

const hasEntriesWithAmounts = (entries = []) =>
  (Array.isArray(entries) ? entries : []).some(
    (entry) => getCreditAmount(entry) !== null
  );

const normalizeCreditEntries = (entries = []) =>
  (Array.isArray(entries) ? entries : [])
    .map((entry) => {
      if (!entry) return null;
      if (typeof entry === "string") {
        return { name: entry };
      }
      if (typeof entry === "object") {
        if (entry.name) return entry;
        if (entry.creditName) {
          return { ...entry, name: entry.creditName };
        }
      }
      return null;
    })
    .filter(Boolean);

const resolveAvailableCredits = (options = {}) => {
  const { primary, override, fallback } = options;
  const fromOverride = normalizeCreditEntries(override);
  if (hasEntriesWithAmounts(fromOverride)) return fromOverride;
  const fromPrimary = normalizeCreditEntries(primary);
  if (hasEntriesWithAmounts(fromPrimary)) return fromPrimary;
  const fromFallback = normalizeCreditEntries(fallback);
  if (hasEntriesWithAmounts(fromFallback)) return fromFallback;
  if (fromOverride.length) return fromOverride;
  if (fromPrimary.length) return fromPrimary;
  return fromFallback;
};

const normalizeCategoryKey = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const FEDERAL_PRECALCULATED_CATEGORY_KEYS = new Set([
  "fez",
  "federalempowermentzone",
  "federalempowermentzones",
  "wotc",
  "workopportunitytaxcredit",
  "workopportunitytaxcredits",
  "fica",
  "ficaextra",
  "ficatiptaxcredit",
  "ficatiptaxcredits",
]);

const PRECALCULATED_CATEGORY_TYPE_OVERRIDES = {
  fez: CREDIT_TYPE_KEYS.FEDERAL_WOTC,
  federalempowermentzone: CREDIT_TYPE_KEYS.FEDERAL_WOTC,
  federalempowermentzones: CREDIT_TYPE_KEYS.FEDERAL_WOTC,
  wotc: CREDIT_TYPE_KEYS.FEDERAL_WOTC,
  workopportunitytaxcredit: CREDIT_TYPE_KEYS.FEDERAL_WOTC,
  workopportunitytaxcredits: CREDIT_TYPE_KEYS.FEDERAL_WOTC,
  fica: CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC,
  ficaextra: CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC,
  ficatiptaxcredit: CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC,
  ficatiptaxcredits: CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC,
};

const allocateFromPrecalculatedCategories = (business, totals) => {
  const categories = business?.precalculatedCategoryTotals;
  if (!categories || typeof categories !== "object") {
    return 0;
  }
  let allocated = 0;
  Object.entries(categories).forEach(([rawKey, rawValue]) => {
    const amount = parseNumericValue(rawValue);
    if (typeof amount !== "number" || amount <= 0) {
      return;
    }
    const normalizedKey = normalizeCategoryKey(rawKey);
    if (!normalizedKey) {
      return;
    }
    const overrideBucket = PRECALCULATED_CATEGORY_TYPE_OVERRIDES[normalizedKey];
    if (overrideBucket) {
      totals[overrideBucket] += amount;
    } else if (FEDERAL_PRECALCULATED_CATEGORY_KEYS.has(normalizedKey)) {
      totals[CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC] += amount;
    } else {
      totals[CREDIT_TYPE_KEYS.STATE] += amount;
    }
    allocated += amount;
  });
  return allocated;
};

const allocateFromPrecalculatedTotals = (business, totals) => {
  const precalc = business?.precalculatedCreditTotals;
  if (!precalc || typeof precalc !== "object") {
    return 0;
  }
  const federalWotcValue = parseNumericValue(precalc.federalWotc);
  const federalNonWotcValue = parseNumericValue(precalc.federalNonWotc);
  const stateValue = parseNumericValue(precalc.state);
  const hasAny =
    typeof federalWotcValue === "number" ||
    typeof federalNonWotcValue === "number" ||
    typeof stateValue === "number";
  if (!hasAny) return 0;
  if (typeof federalWotcValue === "number") {
    totals[CREDIT_TYPE_KEYS.FEDERAL_WOTC] += federalWotcValue;
  }
  if (typeof federalNonWotcValue === "number") {
    totals[CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC] += federalNonWotcValue;
  }
  if (typeof stateValue === "number") {
    totals[CREDIT_TYPE_KEYS.STATE] += stateValue;
  }
  return (
    (typeof federalWotcValue === "number" ? federalWotcValue : 0) +
    (typeof federalNonWotcValue === "number" ? federalNonWotcValue : 0) +
    (typeof stateValue === "number" ? stateValue : 0)
  );
};

export const CREDIT_BUCKET_KEYS = [
  CREDIT_TYPE_KEYS.FEDERAL_WOTC,
  CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC,
  CREDIT_TYPE_KEYS.STATE,
];

export const computeCreditTypeTotals = (
  businesses = [],
  creditOverrides = {}
) => {
  const totals = {
    [CREDIT_TYPE_KEYS.FEDERAL_WOTC]: 0,
    [CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC]: 0,
    [CREDIT_TYPE_KEYS.STATE]: 0,
  };
  (businesses || []).forEach((business) => {
    if (!business) return;
    const businessId =
      business.prospectBusinessId || business.businessId || business.id;
    const override =
      businessId && creditOverrides && creditOverrides[businessId]
        ? creditOverrides[businessId]
        : null;
    const hasOverrideData =
      override &&
      (typeof override.projectedCredit !== "undefined" ||
        (Array.isArray(override.availableCredits) &&
          override.availableCredits.length > 0) ||
        (Array.isArray(override.eligibleCredits) &&
          override.eligibleCredits.length > 0));
    const taxCredits = Array.isArray(business.taxCredits)
      ? business.taxCredits[0]
      : business.taxCredits;
    const projectedCredit = parseNumericValue(
      typeof override?.projectedCredit !== "undefined"
        ? override.projectedCredit
        : taxCredits?.totalProjectedTaxCreditAmount ??
            business.totalProjectedTaxCreditAmount ??
            business.projectedCredit
    );
    const overrideCredits =
      (Array.isArray(override?.availableCredits) && override.availableCredits.length
        ? override.availableCredits
        : override?.eligibleCredits) || null;
    const fallbackCredits =
      (Array.isArray(business.availableCredits) && business.availableCredits.length
        ? business.availableCredits
        : business.eligibleCredits) || null;
    const availableCredits = resolveAvailableCredits({
      primary: taxCredits?.availableTaxCredits,
      override: overrideCredits,
      fallback: fallbackCredits,
    });
    const creditsHaveAmounts = hasEntriesWithAmounts(availableCredits);
    let allocatedFromCategories = 0;
    if (!creditsHaveAmounts && !hasOverrideData) {
      const allocatedFromPrecalcTotals = allocateFromPrecalculatedTotals(
        business,
        totals
      );
      if (allocatedFromPrecalcTotals > 0) {
        return;
      }
      allocatedFromCategories = allocateFromPrecalculatedCategories(
        business,
        totals
      );
    }
    if (!availableCredits.length) {
      if (allocatedFromCategories > 0) {
        if (
          typeof projectedCredit === "number" &&
          !Number.isNaN(projectedCredit) &&
          projectedCredit > allocatedFromCategories
        ) {
          totals[CREDIT_TYPE_KEYS.STATE] +=
            projectedCredit - allocatedFromCategories;
        }
        return;
      }
      if (
        typeof projectedCredit === "number" &&
        !Number.isNaN(projectedCredit) &&
        projectedCredit > 0
      ) {
        totals[CREDIT_TYPE_KEYS.STATE] += projectedCredit;
      }
      return;
    }
    if (!creditsHaveAmounts && allocatedFromCategories > 0) {
      if (
        typeof projectedCredit === "number" &&
        !Number.isNaN(projectedCredit) &&
        projectedCredit > allocatedFromCategories
      ) {
        totals[CREDIT_TYPE_KEYS.STATE] +=
          projectedCredit - allocatedFromCategories;
      }
      return;
    }
    const prepared = availableCredits.map((credit) => ({
      type: resolveCreditTypeFromRecord(credit),
      amount: 0,
      credit,
    }));
    let definedSum = 0;
    prepared.forEach((entry) => {
      const amount = getCreditAmount(entry.credit);
      if (typeof amount === "number" && amount > 0) {
        entry.amount += amount;
        definedSum += amount;
      }
    });
    const remainder =
      typeof projectedCredit === "number" && !Number.isNaN(projectedCredit)
        ? projectedCredit - definedSum
        : 0;
    if (remainder > 0 && prepared.length > 0) {
      const share = remainder / prepared.length;
      prepared.forEach((entry) => {
        entry.amount += share;
      });
    }
    prepared.forEach((entry) => {
      if (entry.amount > 0) {
        if (typeof totals[entry.type] !== "number") {
          totals[entry.type] = 0;
        }
        totals[entry.type] += entry.amount;
      }
    });
  });
  const safeTotals = CREDIT_BUCKET_KEYS.reduce((acc, key) => {
    acc[key] = Number.isFinite(totals[key]) ? totals[key] : 0;
    return acc;
  }, {});
  const totalAmount = CREDIT_BUCKET_KEYS.reduce(
    (sum, key) => sum + safeTotals[key],
    0
  );
  return {
    federalWotc: safeTotals[CREDIT_TYPE_KEYS.FEDERAL_WOTC],
    federalNonWotc: safeTotals[CREDIT_TYPE_KEYS.FEDERAL_NON_WOTC],
    state: safeTotals[CREDIT_TYPE_KEYS.STATE],
    total: Number.isFinite(totalAmount) ? totalAmount : 0,
  };
};
